import pageRoutes from '@routes/pageRoutes.ts';
import type { RouteAccessMeta } from './accessEvaluator.ts';
export { evaluateRouteAccess } from './accessEvaluator.ts';
export type {
  RouteAccessContext,
  RouteAccessDecision,
  RouteAccessDeniedReason,
  RouteAccessMeta,
} from './accessEvaluator.ts';

const normalizePathname = (value: string): string => {
  if (!value) {
    return '/';
  }

  let path = value.startsWith('/') ? value : `/${value}`;
  if (path !== '/') {
    path = path.replace(/\/+$/, '');
  }

  return path || '/';
};

const parseSearchParams = (value?: string): URLSearchParams => {
  if (!value) {
    return new URLSearchParams();
  }

  const raw = value.startsWith('?') ? value.slice(1) : value;
  return new URLSearchParams(raw);
};

const isQuerySubset = (subset: URLSearchParams, actual: URLSearchParams): boolean => {
  let matches = true;
  subset.forEach((value, key) => {
    if (!matches) {
      return;
    }
    const candidates = actual.getAll(key);
    if (!candidates.includes(value)) {
      matches = false;
    }
  });
  return matches;
};

const findRouteByPath = (pathname: string, search: string) => {
  const normalizedPath = normalizePathname(pathname);
  const actualQuery = parseSearchParams(search);

  let bestMatch: { route: (typeof pageRoutes)[number]; score: number } | null = null;

  for (const route of pageRoutes) {
    const [rawPath, rawQuery] = route.path.split('?');
    const normalizedRoutePath = normalizePathname(rawPath);
    const overviewSectionPath = normalizedRoutePath.endsWith('/overview')
      ? normalizePathname(normalizedRoutePath.slice(0, -'/overview'.length))
      : null;
    const matchesExactPath = normalizedRoutePath === normalizedPath;
    const matchesOverviewSection =
      overviewSectionPath !== null &&
      (overviewSectionPath === normalizedPath || normalizedPath.startsWith(`${overviewSectionPath}/`));

    if (!matchesExactPath && !matchesOverviewSection) {
      continue;
    }

    const routeQuery = rawQuery ? parseSearchParams(rawQuery) : new URLSearchParams();
    if (!isQuerySubset(routeQuery, actualQuery)) {
      continue;
    }

    const score = (
      matchesExactPath
        ? normalizedRoutePath.length
        : overviewSectionPath?.length ?? 0
    ) + (rawQuery?.length ?? 0);
    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { route, score };
    }
  }

  return bestMatch?.route ?? null;
};

export const findRouteAccessMeta = (pathname: string, search: string): RouteAccessMeta | null => {
  const route = findRouteByPath(pathname, search);
  if (!route) {
    return null;
  }

  return {
    key: route.key,
    requiredApps: route.requiredApps ?? [],
    requiredAnyApps: route.requiredAnyApps ?? [],
    requiredCapabilities: route.requiredCapabilities ?? [],
    featureFlag: route.featureFlag,
    requiredPermissions: route.requiredPermissions ?? [],
    requiredGroups: route.requiredGroups ?? [],
    requiresStaff: route.requiresStaff !== false,
    navGroup: route.navGroup,
    domain: route.domain,
    allowSuperuserWithoutApp: route.allowSuperuserWithoutApp,
  } satisfies RouteAccessMeta;
};
